import { Clickable } from "@client/components/Clickables";
import { mediaQueries } from "@client/styles/media-queries";
import React from "react";
import { usePreventScroll } from "react-aria";

import { Header2026BodyBlock } from "@reactivated";

import ContactUsForm from "../../../ContactUsForm";

import styles from "./index.module.scss";

export const MENU_ID = "design2026-menu";

const INSTAGRAM_URL = "https://www.instagram.com/thelabdotco/";
const LINKEDIN_URL = "https://www.linkedin.com/company/thelab";
const WELLCOM_URL = "https://www.wellcomww.com/";

interface Props {
    links: Header2026BodyBlock;
    isOpen: boolean;
    onClose: () => void;
    /** The bar's toggle, which gets focus back when Escape closes the panel */
    toggleRef: React.RefObject<HTMLElement | null>;
}

/**
 * The phone's menu panel (≤767). A sibling of the bar, not a child: the bar
 * is transformed, which would make it the panel's containing block.
 */
export const Menu = ({ links, isOpen, onClose, toggleRef }: Props) => {
    usePreventScroll({ isDisabled: !isOpen });

    // Every way out goes through onClose: Escape, and growing past the phone
    React.useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            onClose();
            toggleRef.current?.focus();
        };
        const query = window.matchMedia(mediaQueries.aboveMobile);
        const onChange = () => {
            if (query.matches) onClose();
        };
        onChange();
        window.addEventListener("keydown", onKey);
        query.addEventListener("change", onChange);
        return () => {
            window.removeEventListener("keydown", onKey);
            query.removeEventListener("change", onChange);
        };
    }, [isOpen, onClose, toggleRef]);

    return (
        <div
            id={MENU_ID}
            className={`${styles.menu} ${isOpen ? styles.open : ""}`}
            inert={!isOpen}
        >
            <nav className={styles.nav} aria-label="Menu">
                {links.map(({ id, value }) =>
                    value.url === null ? null : (
                        <Clickable
                            key={id}
                            className={styles.row}
                            href={value.url}
                            onPress={onClose}
                        >
                            <span>{value.display_name ?? value.url}</span>
                        </Clickable>
                    ),
                )}
                <ContactUsForm hooks={false}>
                    <Clickable className={styles.row} onPress={onClose}>
                        <span>Get connected</span>
                    </Clickable>
                </ContactUsForm>
            </nav>
            <div className={styles.foot}>
                <p>
                    ©{new Date().getFullYear()} thelab. Part of{" "}
                    <a
                        href={WELLCOM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        the wellcom group
                    </a>
                </p>
                <div className={styles.social}>
                    <a
                        className={styles.icon}
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="thelab on Instagram"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                        >
                            <rect x="4" y="4" width="16" height="16" rx="4.6" />
                            <circle cx="12" cy="12" r="3.6" />
                            <circle
                                cx="17"
                                cy="7"
                                r=".9"
                                fill="currentColor"
                                stroke="none"
                            />
                        </svg>
                    </a>
                    <a
                        className={styles.icon}
                        href={LINKEDIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="thelab on LinkedIn"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            fill="currentColor"
                        >
                            <rect x="5.2" y="9.6" width="2.7" height="9" />
                            <circle cx="6.55" cy="6.4" r="1.6" />
                            <path d="M10.1 9.6h2.6v1.25a2.9 2.9 0 0 1 2.6-1.4c2 0 3.2 1.25 3.2 3.75v5.4h-2.7v-4.9c0-1.25-.45-2-1.55-2-.95 0-1.45.63-1.45 2v4.9h-2.7z" />
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
};
